import { arrayValue, isArray } from '../array.js';
import nacl from 'tweetnacl';
import { canonicalRegistryJson } from './canonical.js';
import type { ApprovalPolicy } from './approval-contract.js';
export const EVERYDAY_HEALTH_APPROVAL_TRUST_POLICY: ApprovalPolicy = Object.freeze({
    rootId: "hephesapla.everyday-health.approval.local-exact46",
    sequence: 4,
    previousRootSha256: "8e56a71d3b31c80274e220e197b6d2f20118beaf422fc028e2461c65d81f631d",
    keyId: "everyday-health-local-test-ed25519-2026-v4-profit-margin-v2",
    publicKeyBase64: "7ACIRIxVwWRhyC37KdQkdMV8AkrsW9mDFeNZXjTZNlM=",
    approvalRootCanonicalSha256: "fb260dd5700f23525167b632da0f8a3ff4ddd680cbbfb6a816de4a8d453677d3",
});
const SHA256_INITIAL = new Uint32Array([
    0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a,
    0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19,
]);
const SHA256_CONSTANTS = new Uint32Array([
    0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5,
    0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
    0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3,
    0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
    0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc,
    0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
    0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7,
    0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
    0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13,
    0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
    0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3,
    0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
    0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5,
    0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
    0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208,
    0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
]);
function rotateRight(value: number, bits: number) {
    return (value >>> bits) | (value << (32 - bits));
}
function utf8Bytes(value: string) {
    const bytes = [];
    for (const character of value) {
        let codePoint = character.codePointAt(0) ?? 0;
        if (codePoint >= 0xd800 && codePoint <= 0xdfff)
            codePoint = 0xfffd;
        if (codePoint <= 0x7f)
            bytes.push(codePoint);
        else if (codePoint <= 0x7ff) {
            bytes.push(0xc0 | (codePoint >> 6), 0x80 | (codePoint & 0x3f));
        }
        else if (codePoint <= 0xffff) {
            bytes.push(0xe0 | (codePoint >> 12), 0x80 | ((codePoint >> 6) & 0x3f), 0x80 | (codePoint & 0x3f));
        }
        else {
            bytes.push(0xf0 | (codePoint >> 18), 0x80 | ((codePoint >> 12) & 0x3f), 0x80 | ((codePoint >> 6) & 0x3f), 0x80 | (codePoint & 0x3f));
        }
    }
    return Uint8Array.from(bytes);
}
export function sha256Hex(value: string) {
    const source = utf8Bytes(value);
    const paddedLength = Math.ceil((source.length + 1 + 8) / 64) * 64;
    const data = new Uint8Array(paddedLength);
    data.set(source);
    data[source.length] = 0x80;
    const bitLength = source.length * 8;
    const high = Math.floor(bitLength / 4294967296);
    const low = bitLength >>> 0;
    data[paddedLength - 8] = (high >>> 24) & 0xff;
    data[paddedLength - 7] = (high >>> 16) & 0xff;
    data[paddedLength - 6] = (high >>> 8) & 0xff;
    data[paddedLength - 5] = high & 0xff;
    data[paddedLength - 4] = (low >>> 24) & 0xff;
    data[paddedLength - 3] = (low >>> 16) & 0xff;
    data[paddedLength - 2] = (low >>> 8) & 0xff;
    data[paddedLength - 1] = low & 0xff;
    const state = new Uint32Array(SHA256_INITIAL);
    const words = new Uint32Array(64);
    for (let offset = 0; offset < data.length; offset += 64) {
        for (let index = 0; index < 16; index += 1) {
            const wordOffset = offset + index * 4;
            words[index] = (((data[wordOffset] ?? 0) << 24) |
                ((data[wordOffset + 1] ?? 0) << 16) |
                ((data[wordOffset + 2] ?? 0) << 8) |
                (data[wordOffset + 3] ?? 0)) >>> 0;
        }
        for (let index = 16; index < 64; index += 1) {
            const previous15 = words[index - 15] ?? 0;
            const previous2 = words[index - 2] ?? 0;
            const sigma0 = rotateRight(previous15, 7) ^ rotateRight(previous15, 18) ^ (previous15 >>> 3);
            const sigma1 = rotateRight(previous2, 17) ^ rotateRight(previous2, 19) ^ (previous2 >>> 10);
            words[index] = ((words[index - 16] ?? 0) + sigma0 + (words[index - 7] ?? 0) + sigma1) >>> 0;
        }
        let a = state[0] ?? 0;
        let b = state[1] ?? 0;
        let c = state[2] ?? 0;
        let d = state[3] ?? 0;
        let e = state[4] ?? 0;
        let f = state[5] ?? 0;
        let g = state[6] ?? 0;
        let h = state[7] ?? 0;
        for (let index = 0; index < 64; index += 1) {
            const upperSigma1 = rotateRight(e, 6) ^ rotateRight(e, 11) ^ rotateRight(e, 25);
            const choose = (e & f) ^ (~e & g);
            const temporary1 = (h + upperSigma1 + choose + (SHA256_CONSTANTS[index] ?? 0) + (words[index] ?? 0)) >>> 0;
            const upperSigma0 = rotateRight(a, 2) ^ rotateRight(a, 13) ^ rotateRight(a, 22);
            const majority = (a & b) ^ (a & c) ^ (b & c);
            const temporary2 = (upperSigma0 + majority) >>> 0;
            h = g;
            g = f;
            f = e;
            e = (d + temporary1) >>> 0;
            d = c;
            c = b;
            b = a;
            a = (temporary1 + temporary2) >>> 0;
        }
        state[0] = ((state[0] ?? 0) + a) >>> 0;
        state[1] = ((state[1] ?? 0) + b) >>> 0;
        state[2] = ((state[2] ?? 0) + c) >>> 0;
        state[3] = ((state[3] ?? 0) + d) >>> 0;
        state[4] = ((state[4] ?? 0) + e) >>> 0;
        state[5] = ((state[5] ?? 0) + f) >>> 0;
        state[6] = ((state[6] ?? 0) + g) >>> 0;
        state[7] = ((state[7] ?? 0) + h) >>> 0;
    }
    return Array.from(state, (word) => word.toString(16).padStart(8, "0")).join("");
}
function decodeBase64(value: string) {
    if (value.length === 0 || value.length % 4 !== 0 || !/^[A-Za-z0-9+/]*={0,2}$/.test(value)) {
        throw new Error("Günlük/sağlık trust Base64 verisi geçersiz.");
    }
    const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/";
    const bytes = [];
    for (let index = 0; index < value.length; index += 4) {
        const chunk = value.slice(index, index + 4);
        const values = [chunk[0], chunk[1], chunk[2], chunk[3]].map((character) => character === "=" ? 0 : alphabet.indexOf(character ?? ""));
        if (values.some((item) => item < 0))
            throw new Error("Günlük/sağlık trust Base64 verisi geçersiz.");
        const packed = (values[0] << 18) | (values[1] << 12) | (values[2] << 6) | values[3];
        bytes.push((packed >> 16) & 0xff);
        if (chunk[2] !== "=")
            bytes.push((packed >> 8) & 0xff);
        if (chunk[3] !== "=")
            bytes.push(packed & 0xff);
    }
    return Uint8Array.from(bytes);
}
function isRecord(value: unknown): value is Readonly<Record<string, unknown>> {
    return value !== null && typeof value === "object" && !isArray(value);
}
function requireRecord(value: unknown, label: string): Readonly<Record<string, unknown>> {
    if (!isRecord(value))
        throw new Error(`${label} nesne olmalıdır.`);
    return value;
}
function requireString(record: Readonly<Record<string, unknown>>, field: string, label: string) {
    const value = record[field];
    if (typeof value !== "string" || value.length === 0)
        throw new Error(`${label}.${field} geçersiz.`);
    return value;
}
function requireSha256(value: unknown, label: string) {
    if (typeof value !== "string" || !/^[a-f0-9]{64}$/.test(value)) {
        throw new Error(`${label} SHA-256 değeri geçersiz.`);
    }
    return value;
}
function parseApprovalRoot(value: unknown) {
    const root = requireRecord(value, "approvalRoot");
    const allowedKeys = [
        "formatVersion", "rootId", "sequence", "previousRootSha256", "keyId", "receiptId",
        "receiptCanonicalSha256", "oracleId", "oracleCanonicalSha256", "signatureBase64",
    ].sort();
    if (JSON.stringify(Object.keys(root).sort()) !== JSON.stringify(allowedKeys)) {
        throw new Error("Günlük/sağlık approval root alanları exact sözleşmeyle eşleşmelidir.");
    }
    if (root.formatVersion !== 1 || typeof root.sequence !== 'number' || !Number.isSafeInteger(root.sequence) || root.sequence < 1) {
        throw new Error("Günlük/sağlık approval root sürüm veya sequence değeri geçersiz.");
    }
    if (root.previousRootSha256 !== null)
        requireSha256(root.previousRootSha256, "previousRootSha256");
    return {
        formatVersion: 1,
        rootId: requireString(root, "rootId", "approvalRoot"),
        sequence: root.sequence,
        previousRootSha256: root.previousRootSha256 === null ? null : requireSha256(root.previousRootSha256, "previousRootSha256"),
        keyId: requireString(root, "keyId", "approvalRoot"),
        receiptId: requireString(root, "receiptId", "approvalRoot"),
        receiptCanonicalSha256: requireSha256(root.receiptCanonicalSha256, "receiptCanonicalSha256"),
        oracleId: requireString(root, "oracleId", "approvalRoot"),
        oracleCanonicalSha256: requireSha256(root.oracleCanonicalSha256, "oracleCanonicalSha256"),
        signatureBase64: requireString(root, "signatureBase64", "approvalRoot"),
    };
}
function canonicalUnsignedApprovalRoot(root: ReturnType<typeof parseApprovalRoot>) {
    return canonicalRegistryJson({
        formatVersion: root.formatVersion,
        rootId: root.rootId,
        sequence: root.sequence,
        previousRootSha256: root.previousRootSha256,
        keyId: root.keyId,
        receiptId: root.receiptId,
        receiptCanonicalSha256: root.receiptCanonicalSha256,
        oracleId: root.oracleId,
        oracleCanonicalSha256: root.oracleCanonicalSha256,
    });
}
export function assertEverydayHealthOracleHashes(receiptValue: unknown, oracleValue: unknown, expectedOracleCanonicalSha256: string) {
    const receipt = requireRecord(receiptValue, "receipt");
    const oracle = requireRecord(oracleValue, "oracle");
    const actualOracleSha = sha256Hex(canonicalRegistryJson(oracle));
    const receiptOracle = requireRecord(receipt.oracle, "receipt.oracle");
    if (actualOracleSha !== expectedOracleCanonicalSha256 ||
        receiptOracle.canonicalSha256 !== actualOracleSha) {
        throw new Error("Günlük/sağlık actual oracle canonical SHA değeri trust root/receipt ile eşleşmiyor.");
    }
    const receiptTools = arrayValue(receipt.tools);
    const oracleRecords = arrayValue(oracle.records);
    const receiptRecordSha = requireRecord(receiptOracle.recordSha256, "receipt.oracle.recordSha256");
    if (receiptTools === null || oracleRecords === null || receiptTools.length !== oracleRecords.length) {
        throw new Error("Günlük/sağlık receipt/oracle kayıt sayısı eşleşmiyor.");
    }
    const receiptById = new Map<string, Readonly<Record<string, unknown>>>();
    const receiptEntries: readonly unknown[] = receiptTools;
    for (const item of receiptEntries) {
        const record = requireRecord(item, "receipt.tools[]");
        const toolId = requireString(record, "runtimeToolId", "receipt.tools[]");
        if (receiptById.has(toolId))
            throw new Error("Günlük/sağlık receipt araç kimliği tekrarlı.");
        const contractSha = requireSha256(record.contractCanonicalSha256, `${toolId}.contractCanonicalSha256`);
        if (sha256Hex(canonicalRegistryJson(record.contract)) !== contractSha) {
            throw new Error(`${toolId} receipt contract SHA değeri actual contract ile eşleşmiyor.`);
        }
        receiptById.set(toolId, record);
    }
    const oracleIds = new Set<string>();
    const oracleEntries: readonly unknown[] = oracleRecords;
    for (const item of oracleEntries) {
        const record = requireRecord(item, "oracle.records[]");
        const toolId = requireString(record, "runtimeToolId", "oracle.records[]");
        if (oracleIds.has(toolId))
            throw new Error("Günlük/sağlık oracle araç kimliği tekrarlı.");
        oracleIds.add(toolId);
        const receiptRecord = receiptById.get(toolId);
        if (receiptRecord === undefined)
            throw new Error(`${toolId} için receipt kaydı yok.`);
        const actualRecordSha = sha256Hex(canonicalRegistryJson(record));
        if (receiptRecordSha[toolId] !== actualRecordSha ||
            receiptRecord.oracleRecordSha256 !== actualRecordSha) {
            throw new Error(`${toolId} actual oracle record SHA değeri receipt ile eşleşmiyor.`);
        }
    }
    if (oracleIds.size !== receiptById.size ||
        Object.keys(receiptRecordSha).length !== oracleIds.size ||
        Object.keys(receiptRecordSha).some((toolId) => !oracleIds.has(toolId))) {
        throw new Error("Günlük/sağlık oracle record SHA kapsamı exact araç kümesiyle eşleşmiyor.");
    }
    if (canonicalRegistryJson(oracle.coreSourceSha256) !== canonicalRegistryJson(receipt.frozenCoreSourceSha256)) {
        throw new Error("Günlük/sağlık frozen core SHA haritası receipt/oracle arasında eşleşmiyor.");
    }
}
export function assertEverydayHealthApprovalIntegrity(approvalRootValue: unknown, receiptValue: unknown, oracleValue: unknown, policy: ApprovalPolicy) {
    const root = parseApprovalRoot(approvalRootValue);
    if (root.rootId !== policy.rootId ||
        root.keyId !== policy.keyId ||
        root.sequence !== policy.sequence ||
        root.previousRootSha256 !== policy.previousRootSha256) {
        throw new Error("Günlük/sağlık approval root kimlik, sequence veya rotasyon zinciri geçersiz.");
    }
    if (sha256Hex(canonicalRegistryJson(root)) !== policy.approvalRootCanonicalSha256) {
        throw new Error("Günlük/sağlık approval root bağımsız canonical SHA pininden saptı.");
    }
    const publicKey = decodeBase64(policy.publicKeyBase64);
    const signature = decodeBase64(root.signatureBase64);
    if (publicKey.length !== nacl.sign.publicKeyLength || signature.length !== nacl.sign.signatureLength) {
        throw new Error("Günlük/sağlık Ed25519 anahtar veya imza uzunluğu geçersiz.");
    }
    if (!nacl.sign.detached.verify(utf8Bytes(canonicalUnsignedApprovalRoot(root)), signature, publicKey)) {
        throw new Error("Günlük/sağlık offline Ed25519 approval imzası geçersiz.");
    }
    const receipt = requireRecord(receiptValue, "receipt");
    const oracle = requireRecord(oracleValue, "oracle");
    if (receipt.receiptId !== root.receiptId || oracle.oracleId !== root.oracleId) {
        throw new Error("Günlük/sağlık approval root receipt/oracle kimliğiyle eşleşmiyor.");
    }
    const actualReceiptSha = sha256Hex(canonicalRegistryJson(receipt));
    if (actualReceiptSha !== root.receiptCanonicalSha256) {
        throw new Error("Günlük/sağlık receipt bağımsız imzalı trust root SHA değerinden saptı.");
    }
    const receiptOracle = requireRecord(receipt.oracle, "receipt.oracle");
    if (receiptOracle.oracleId !== root.oracleId) {
        throw new Error("Günlük/sağlık receipt oracle kimliği trust root ile eşleşmiyor.");
    }
    assertEverydayHealthOracleHashes(receipt, oracle, root.oracleCanonicalSha256);
}
export function assertPinnedEverydayHealthApprovalIntegrity(approvalRootValue: unknown, receiptValue: unknown, oracleValue: unknown) {
    assertEverydayHealthApprovalIntegrity(approvalRootValue, receiptValue, oracleValue, EVERYDAY_HEALTH_APPROVAL_TRUST_POLICY);
}
